import { Box, Grid } from "@mui/material";
import i18n from "i18next";
import { useEffect, useState } from "react";

import { isPastDate } from "../../app/common";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import OverlaySpinner from "../../common/components/functional/overlaySpinner/OverlaySpinner";
import { Nullable } from "../../common/types/nullable";
import useIsLoading from "../../hooks/useIsLoading";
import { Talent } from "../talents/talentAPI";
import { getAttachmentsFromLocalStorage } from "../talents/talentsSlice";
import {
  fetchWorkHistoriesByTalentId,
  fetchWorkHistorySummaryByTalentId,
  selectAllWorkhistories,
} from "../workHistory/WorkHistoriesSlice";
import { WorkHistoryById } from "../workHistory/types/queries";
import TalentProfileTabContent from "./TalentProfileTabContent";
import Comments from "./comments/Comments";
import TalentDetailsEditFormContainer from "./editForms/TalentDetailsEditFormContainer";
import TalentHR1 from "./talentHrRepresentative/TalentHr";
import TalentProfileSummary from "./talentProfileSummary/TalentProfileSummary";
import UserMessagesList from "./userMessages/UserMessagesList";

export type ContentKey =
  | "contactDetails"
  | "workHistory_add"
  | "workHistory_edit"
  | "workTitles"
  | "skills"
  | "educations_edit"
  | "educations_add"
  | "languages_edit"
  | "languages_add"
  | "shiftSalaries_add"
  | "shiftSalaries_edit"
  | "workPermits_add"
  | "workPermits_edit"
  | "drivingLicenses_add"
  | "drivingLicenses_edit"
  | "trainings_add"
  | "trainings_edit"
  | null;

export type EditMode = {
  enabled: boolean;
  content: ContentKey;
  id: string;
  fixedType?: string;
};

type Props = {
  talent: Talent;
};

export const TalentDetails = (props: Props) => {
  // FIXME: refactor this to use contentKey (same way as rest of the code)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedEducationId, setSelectedEducationId] =
    useState<Nullable<string>>(null);

  const [editMode, setEditMode] = useState<EditMode>({
    enabled: false,
    content: null,
    id: "",
  });

  const { talent } = props;

  const isLoading = useIsLoading([
    "talents",
    "expertUsers",
    "workHistories",
    "employment.attributes",
    "talentComments",
    "customerOrganizations",
  ]);

  const language = i18n.language;

  const dispatch = useAppDispatch();

  // Show only work history items that start date is in the past
  const workHistories = useAppSelector((state) =>
    selectAllWorkhistories(state),
  ).filter((workHistory) => isPastDate(new Date(workHistory.startDate)));

  const handleOpenEditModal = (
    content: ContentKey,
    id: string = "",
    fixedType: string | undefined = undefined,
  ) => setEditMode({ enabled: true, content, id, fixedType });

  const handleCloseEditModal = () => {
    setEditMode({ ...editMode, enabled: false, fixedType: undefined });
    dispatch(getAttachmentsFromLocalStorage(talent.id));

    setSelectedEducationId(null);
  };

  const handleCloseOnExited = () => {
    setEditMode({
      enabled: false,
      content: null,
      id: "",
      fixedType: undefined,
    });
  };

  const handleSelectEducation = (id: string) => setSelectedEducationId(id);

  useEffect(() => {
    const talentId = talent?.id;

    const queryParameters: WorkHistoryById = {
      id: talentId,
      lang: language,
    };

    if (talentId) {
      dispatch(fetchWorkHistoriesByTalentId(queryParameters));
      dispatch(fetchWorkHistorySummaryByTalentId(queryParameters));
    }
  }, [talent?.id, dispatch, language]);

  return (
    <>
      <OverlaySpinner isLoading={isLoading} />
      <Grid container>
        <TalentProfileSummary talent={talent} />
        <Grid item xs={3} sx={{ position: "relative" }}>
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            <UserMessagesList talentId={talent.id} />
          </Box>
        </Grid>
        <TalentHR1 isLoading={isLoading} />
        <Grid item xs={9}>
          <TalentProfileTabContent
            workHistories={workHistories}
            handleOpenEditModal={handleOpenEditModal}
            handleSelectEducation={handleSelectEducation}
            talent={talent}
          />
          <TalentDetailsEditFormContainer
            talent={talent}
            editMode={editMode}
            handleCloseEditModal={handleCloseEditModal}
            handleCloseOnExited={handleCloseOnExited}
          />
        </Grid>
        <Grid item xs={3}>
          <Comments />
        </Grid>
      </Grid>
    </>
  );
};
